import DrawerLayout from "@/Components/Layout/Drawer";
import { apiErrorParser, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import type { OrderDetailResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { CuToman, FiArrowRight } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { Button, Card, Divider, Drawer, Flex, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { FC } from "react";

interface PaymentBeforeMaturity {
  open: boolean;
  onClose: () => void;
  data: OrderDetailResponse;
  trackingCode: string;
  installmentCodes: string[];
}
const OrderDetailsPagePaymentBeforeMaturity: FC<PaymentBeforeMaturity> = ({
  onClose,
  open,
  data,
  installmentCodes,
  trackingCode,
}) => {
  const { t } = useAppTranslation();

  const [paymentStatus, payment] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.installmentGroupPayment({
        trackingCode,
        installmentCodes,
      });
      if (res.url) {
        window.location.replace(res.url);
      }
    } catch (error: any) {
      const parsedError = await apiErrorParser(error);
      if (parsedError.parsed) {
        throw parsedError.parsed;
      }
      throw parsedError.original;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(installmentCodes), trackingCode]);

  return (
    <Drawer
      open={open}
      title={
        <Flex align="center" gap={"small"}>
          <Button icon={<FiArrowRight width={14} height={14} />} type="text" onClick={onClose} />
          <Typography.Text>{t("common.invoice")}</Typography.Text>
        </Flex>
      }
      onClose={onClose}
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      classNames={{
        header: "tw:py-3! tw:px-4!",
      }}
      styles={{ body: { padding: "0" } }}
    >
      <DrawerLayout className="tw:h-full">
        <Flex vertical className="tw:h-full">
          <Flex vertical className="tw:px-4! tw:pt-8!" gap={"middle"}>
            <Typography.Text>{t("common.paymentInfo")}</Typography.Text>
            <Card size="small">
              <Flex align="center" vertical className="tw:w-full" gap={"small"}>
                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{`${t("common.refundTotal")} :`}</Typography.Text>

                  <Flex align="center">
                    <Typography.Text className="tw:m-0!">
                      {formatLocalizedNumber({ value: data.remainedInstallmentAmount ?? 0 })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Flex>

                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{`${t("common.yourProfit")} :`}</Typography.Text>

                  <Flex align="center">
                    <Typography.Text className="tw:m-0!">
                      {formatLocalizedNumber({ value: data.discountOnPayOff ?? 0 })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Flex>

                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{`${t("common.totalInstallmentsAfterDiscount")} :`}</Typography.Text>

                  <Flex align="center">
                    <Typography.Text className="tw:m-0!">
                      {formatLocalizedNumber({
                        value: (data.remainedInstallmentAmount ?? 0) - (data.discountOnPayOff ?? 0),
                      })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          </Flex>

          <Flex vertical className="tw:mt-auto tw:w-full">
            <Divider className="tw:m-0!" />
            <Flex className="tw:w-full tw:px-4! tw:py-2!" align="center" justify="space-between">
              <Flex vertical>
                <Typography.Text type="secondary">{t("common.payableAmount")}</Typography.Text>

                <Flex align="center">
                  <Typography.Text>
                    {formatLocalizedNumber({
                      value: (data.remainedInstallmentAmount ?? 0) - (data.discountOnPayOff ?? 0),
                    })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>

              <Button loading={paymentStatus.loading} className="tw:px-10!" type="primary" onClick={payment}>
                {t("common.pay")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </DrawerLayout>
    </Drawer>
  );
};

export default OrderDetailsPagePaymentBeforeMaturity;
